import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

import { IBaseProps } from '@rbi-ctg/frontend';
import { usePrevious } from 'hooks/use-previous';
import { useAuthContext } from 'state/auth';
import { useFlag } from 'state/launchdarkly';
import { getConfigValue } from 'utils/environment';
import { LaunchDarklyFlag } from 'utils/launchdarkly';

declare global {
  interface Window {
    Pypestream?: any;
  }
}

export const PYPESTREAM_SCRIPT_ID = 'pypestream-script';
export const PYPESTREAM_INTERFACE_ID = 'pypestream-interface';

const defaultContext = {
  show: () => {},
  hide: () => {},
  declareContainerReady: () => {},
  injectScript: () => {},
  visible: false,
  loading: true,
};

const PypestreamContext = createContext(defaultContext);

export const usePypestreamContext = () => useContext(PypestreamContext);

const { scriptUrl, appId } = getConfigValue('pypestream');

export const PypestreamProvider = ({ children }: IBaseProps) => {
  const [visible, setVisible] = useState(false);
  const [containerReady, setContainerReady] = useState(false);
  const [scriptReady, setScriptReady] = useState(false);
  const [hasBootedOnce, setHasBootedOnce] = useState(false);

  const { user, loading: authLoading } = useAuthContext();
  const enablePypestream = useFlag(LaunchDarklyFlag.ENABLE_PYPESTREAM);
  const authReady = !authLoading;
  const cognitoId = user?.cognitoId ?? '';
  const previousCognitoId = usePrevious(cognitoId);
  const cognitoIdHasChanged = cognitoId !== previousCognitoId;

  const declareContainerReady = useCallback(() => setContainerReady(true), []);

  const loading = !authReady || !scriptReady || !containerReady;

  const show = useCallback(() => {
    setVisible(true);
    window?.Pypestream?.show();
  }, []);

  const hide = useCallback(() => {
    setVisible(false);
    window?.Pypestream?.hide();
  }, []);

  const injectScript = useCallback(() => {
    if (!document.getElementById(PYPESTREAM_SCRIPT_ID) && enablePypestream) {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = `${scriptUrl}?ts=${Date.now()}`;
      script.async = true;
      script.id = PYPESTREAM_SCRIPT_ID;
      script.dataset.testid = PYPESTREAM_SCRIPT_ID;
      script.onload = () => setScriptReady(true);
      document.body.appendChild(script);
    }
  }, [enablePypestream]);

  useEffect(() => {
    if (!loading && !hasBootedOnce) {
      window?.Pypestream?.boot(
        {
          APP_ID: appId,
          token: cognitoId,
          language: 'en',
          play_chime: false,
        },
        document.getElementById(PYPESTREAM_INTERFACE_ID)
      );
      setHasBootedOnce(true);
    }
  }, [cognitoId, hasBootedOnce, loading]);

  useEffect(() => {
    if (hasBootedOnce && cognitoIdHasChanged) {
      // end the current chat
      window?.Pypestream?.shutdown();
      // then start a new one for the new user
      window?.Pypestream?.boot(
        {
          APP_ID: appId,
          token: cognitoId,
          language: 'en',
          play_chime: false,
        },
        document.getElementById(PYPESTREAM_INTERFACE_ID)
      );
    }
  }, [cognitoId, cognitoIdHasChanged, hasBootedOnce]);

  return (
    <PypestreamContext.Provider
      value={{ show, hide, visible, declareContainerReady, injectScript, loading }}
    >
      {children}
    </PypestreamContext.Provider>
  );
};
